import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../utils/checkoutCalculations';
import { paymentActions } from '../../../_actions';
import toast from '../../shared/Toast';

/**
 * CardReaderScreen
 * Two modes:
 *   'select' - Show readers, connect, charge
 *   'pair'   - Pair a new reader (replaces the whole screen)
 */
const CardReaderScreen = ({ cardReaders, terminal, amount, onBack, onClose, onPaymentComplete, currencySymbol, dispatch, onReadersRefresh }) => {
  const fmt = (a) => formatCurrency(a, currencySymbol);
  const [mode, setMode] = useState('select'); // 'select' | 'pair'
  const [pairingCode, setPairingCode] = useState('');
  const [deviceLabel, setDeviceLabel] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  // Starting = creating the payment on our server, before the reader takes
  // over. A refusal there (e.g. stylist's payouts not set up) arrives as a
  // thrown Error from onPaymentComplete and is shown here, not swallowed.
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState(null);

  const handleCharge = async () => {
    if (isStarting) return;
    setIsStarting(true);
    setStartError(null);
    if (terminal.clearError) terminal.clearError();
    try {
      await onPaymentComplete();
    } catch (e) {
      setStartError(e?.message || "Couldn't start the card payment. Please try again.");
    }
    setIsStarting(false);
  };

  // Auto-connect if only one reader
  useEffect(() => {
    if (cardReaders.length === 1 && !terminal.isConnected && !terminal.isConnecting) {
      terminal.connectReader(cardReaders[0], cardReaders[0].location);
    }
  }, [cardReaders]);

  const handleSelectReader = (reader) => {
    if (terminal.isConnecting || terminal.isProcessing) return;
    terminal.connectReader(reader, reader.location);
  };

  const handleAddReader = async () => {
    if (!pairingCode.trim()) return;
    setIsPairing(true);
    try {
      const response = await dispatch(paymentActions.addTerminal({
        registrationCode: pairingCode.trim(),
        label: deviceLabel.trim() || 'Card Reader',
      }));
      if (response?.data?.statusCode === 1) {
        toast.success('Card reader paired');
        setPairingCode('');
        setDeviceLabel('');
        setMode('select');
        if (onReadersRefresh) onReadersRefresh();
      } else {
        toast.error('Failed to pair. Check the code and try again.');
      }
    } catch {
      toast.error('Failed to pair reader.');
    }
    setIsPairing(false);
  };

  // --- PAIR MODE ---
  if (mode === 'pair') {
    return (
      <div className="co-cr">
        <div className="co-screen-header">
          <button className="co-back-btn" onClick={() => setMode('select')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h2 className="co-screen-title">Pair New Reader</h2>
        </div>

        <div className="co-section">
          <div className="co-cr-steps">
            <p className="co-cr-step"><strong>1.</strong> Turn on your BBPOS WisePOS E and connect to the same WiFi network</p>
            <p className="co-cr-step"><strong>2.</strong> Swipe from the left, go to Settings, enter admin pin <strong>07139</strong></p>
            <p className="co-cr-step"><strong>3.</strong> Select "Generate pairing code" and enter the 3-word code below</p>
          </div>

          <div className="co-cr-field">
            <label className="co-cr-label">Pairing Code</label>
            <input
              className="co-cr-input"
              type="text"
              placeholder="e.g. frog-kiss-settle"
              value={pairingCode}
              onChange={e => setPairingCode(e.target.value)}
              autoFocus
            />
          </div>

          <div className="co-cr-field">
            <label className="co-cr-label">Device Name</label>
            <input
              className="co-cr-input"
              type="text"
              placeholder="e.g. Front Desk Reader"
              value={deviceLabel}
              onChange={e => setDeviceLabel(e.target.value)}
            />
          </div>
        </div>

        <div className="co-pl-footer">
          <button className="bdp-btn bdp-btn--secondary" onClick={() => setMode('select')}>
            Cancel
          </button>
          <button
            className="bdp-btn bdp-btn--primary"
            onClick={handleAddReader}
            disabled={!pairingCode.trim() || isPairing}
          >
            {isPairing ? 'Pairing...' : 'Pair Reader'}
          </button>
        </div>
      </div>
    );
  }

  // --- SELECT MODE ---
  return (
    <div className="co-cr">
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={onBack} disabled={terminal.isProcessing}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">Card Reader</h2>
      </div>

      {/* Amount */}
      <div className="co-pl-amount">
        <span className="co-pl-amount-value">{fmt(amount)}</span>
      </div>

      {/* Status messages */}
      {terminal.isConnected && terminal.connectedReader && (
        <div className="co-cr-status co-cr-status--connected">
          <div className="co-cr-status-dot co-cr-status-dot--connected" />
          <span>Connected to {terminal.connectedReader.label || terminal.connectedReader.serial_number}</span>
        </div>
      )}

      {terminal.isConnecting && (
        <div className="co-cr-status">
          <div className="co-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
          <span>Connecting...</span>
        </div>
      )}

      {terminal.isProcessing && (
        <div className="co-cr-status co-cr-status--processing">
          <div className="co-spinner" style={{ width: 18, height: 18, borderWidth: 2 }} />
          <span>Waiting for card...</span>
        </div>
      )}

      {terminal.error && (
        <div className="co-cr-status co-cr-status--error">
          <span>{terminal.error}</span>
          <button className="co-cr-retry" onClick={() => terminal.clearError()}>Dismiss</button>
        </div>
      )}

      {startError && !terminal.error && (
        <div className="co-cr-status co-cr-status--error">
          <span>{startError}</span>
          <button className="co-cr-retry" onClick={() => setStartError(null)}>Dismiss</button>
        </div>
      )}

      {/* Reader list */}
      {!terminal.isProcessing && (
        <div className="co-section">
          <div className="co-section-title">
            {cardReaders.length === 0 ? 'No Readers Paired' : terminal.isConnected ? 'Readers' : 'Select a Reader'}
          </div>

          {cardReaders.length > 0 && (
            <div className="co-cr-list">
              {cardReaders.map((reader, i) => {
                const isActive = terminal.connectedReader?.serial_number === reader.serial_number;
                return (
                  <button
                    key={i}
                    className={`co-cr-reader ${isActive ? 'co-cr-reader--active' : ''}`}
                    onClick={() => handleSelectReader(reader)}
                    disabled={terminal.isConnecting}
                  >
                    <div className="co-cr-reader-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="18" rx="3" /><line x1="8" y1="15" x2="16" y2="15" /><circle cx="12" cy="10" r="2" />
                      </svg>
                    </div>
                    <div className="co-cr-reader-info">
                      <span className="co-cr-reader-label">{reader.label || 'Card Reader'}</span>
                      <span className="co-cr-reader-serial">{reader.serial_number}</span>
                    </div>
                    {isActive && (
                      <div className="co-cr-reader-check">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#34C759" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,8 7,12 13,4" />
                        </svg>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <button className="co-add-item-btn" onClick={() => setMode('pair')} style={{ marginTop: cardReaders.length > 0 ? 8 : 0 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#007EA0" strokeWidth="2" strokeLinecap="round">
              <line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" />
            </svg>
            Add new card reader
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="co-pl-footer">
        {/* Cancel mirrors the header X exactly (same handler, never disabled)
            — it used to call onBack with disabled={isProcessing}, which made
            it dead precisely while "Waiting for card...". */}
        <button className="bdp-btn bdp-btn--secondary" onClick={onClose || onBack}>
          Cancel
        </button>
        <button
          className="bdp-btn bdp-btn--primary"
          onClick={handleCharge}
          disabled={!terminal.isConnected || terminal.isProcessing || terminal.isConnecting || isStarting}
        >
          {terminal.isProcessing ? 'Processing...' : isStarting ? 'Starting...' : `Charge ${fmt(amount)}`}
        </button>
      </div>
    </div>
  );
};

export default CardReaderScreen;
